/*
  Editor and display helpers of the "table" question.

  The formula engine itself is tableFormula.js (a copy kept identical to
  kevi-forms). This file holds what only the admin needs: structural edits
  that keep formulas pointing at the right cells, paste from spreadsheets, the
  save-time checks of the contract that the engine does not do, and helpers to
  render a stored answer.

  Cell targets used across the editor:
    {kind: "data", row, col}     fixed: row = item index; dynamic: row = 0 (template)
    {kind: "summary", row, col}  row = summary index
*/
import {
    columnLetter,
    computeTable,
    hasFormula,
    parseFormula,
    shiftFormula,
    validateTableSettings,
} from "./tableFormula";

export const COLUMN_TYPES = ["text", "number", "select", "date"];
export const MAX_COLUMNS = 30;
export const MAX_FIXED_ROWS = 100;
export const MAX_SUMMARY_ROWS = 5;
export const MAX_DYNAMIC_ROWS = 200;
export const DEFAULT_DYNAMIC_MAX = 50;
export const MIN_WIDTH_DEFAULTS = { number: 64, date: 120, select: 120, text: 160 };

export { columnLetter };

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

export function isBlank(value) {
    return value === null || value === undefined || (typeof value === "string" && value.trim() === "");
}

export function defaultMinWidth(column) {
    return MIN_WIDTH_DEFAULTS[(column && column.type) || "text"] || 160;
}

export function newColumn(label, type = "text") {
    return { label: label || "", type };
}

/* A small table to start from, so the editor never opens empty. */
export function defaultTableSettings(labels = {}) {
    return {
        columns: [
            newColumn(labels.item || "Item", "text"),
            newColumn(labels.quantity || "Quantidade", "number"),
        ],
        rows: {
            mode: "dynamic",
            min: 1,
            max: DEFAULT_DYNAMIC_MAX,
            addLabel: labels.addLabel || "",
            template: { cells: [null, null] },
            summary: [],
        },
    };
}

/* Fills the gaps of settings loaded from the API so the editor can bind to them. */
export function prepareTableSettings(raw, labels = {}) {
    if (!raw || !Array.isArray(raw.columns) || !raw.columns.length || !raw.rows) {
        return defaultTableSettings(labels);
    }
    const settings = clone(raw);
    const n = settings.columns.length;
    settings.columns = settings.columns.map((col) => {
        const column = Object.assign({ label: "", type: "text" }, col || {});
        if (column.type === "select") {
            column.options = (column.options || []).map((option, index) => ({
                text: option.text || "",
                order: index + 1,
                removed: !!option.removed,
                new: false,
            }));
        }
        return column;
    });
    const fitCells = (cells) => {
        const out = (cells || []).slice(0, n);
        while (out.length < n) out.push(null);
        return out;
    };
    const rows = settings.rows;
    rows.mode = rows.mode === "fixed" ? "fixed" : "dynamic";
    if (rows.mode === "fixed") {
        rows.items = (rows.items || []).map((item) => ({
            label: (item && item.label) || "",
            cells: fitCells(item && item.cells),
        }));
        if (!rows.items.length) rows.items.push({ label: "", cells: fitCells([]) });
    } else {
        rows.template = { cells: fitCells(rows.template && rows.template.cells) };
        rows.min = rows.min === undefined || rows.min === null ? 1 : rows.min;
        rows.max = rows.max === undefined || rows.max === null ? DEFAULT_DYNAMIC_MAX : rows.max;
        rows.addLabel = rows.addLabel || "";
    }
    rows.summary = (rows.summary || []).map((s) => ({
        label: (s && s.label) || "",
        cells: fitCells(s && s.cells),
    }));
    return settings;
}

export function editorDataRowCount(settings) {
    const rows = (settings && settings.rows) || {};
    return rows.mode === "fixed" ? (rows.items || []).length : 1;
}

/* Editor row number (1-based, the one used in references) of a target. */
export function editorRowOf(settings, target) {
    return target.kind === "summary"
        ? editorDataRowCount(settings) + target.row + 1
        : target.row + 1;
}

export function cellRefOf(settings, target) {
    return columnLetter(target.col) + editorRowOf(settings, target);
}

/* Inverse of editorRowOf. */
export function targetOfEditorRow(settings, editorRow, col) {
    const dataRows = editorDataRowCount(settings);
    if (editorRow >= 1 && editorRow <= dataRows) return { kind: "data", row: editorRow - 1, col };
    const k = editorRow - dataRows - 1;
    if (k >= 0 && k < ((settings.rows && settings.rows.summary) || []).length) {
        return { kind: "summary", row: k, col };
    }
    return null;
}

export function rowCellsOf(settings, target) {
    const rows = settings.rows;
    if (target.kind === "summary") return rows.summary[target.row].cells;
    if (rows.mode === "fixed") return rows.items[target.row].cells;
    return rows.template.cells;
}

export function cellCfgOf(settings, target) {
    const cells = rowCellsOf(settings, target);
    return (cells && cells[target.col]) || null;
}

function allCellRows(settings) {
    const rows = settings.rows;
    const out = [];
    if (rows.mode === "fixed") (rows.items || []).forEach((item) => out.push(item.cells));
    else if (rows.template) out.push(rows.template.cells);
    (rows.summary || []).forEach((s) => out.push(s.cells));
    return out;
}

function compactCfg(cfg) {
    if (!cfg) return null;
    const out = {};
    if (hasFormula(cfg)) {
        out.formula = cfg.formula.trim();
        return out;
    }
    if (!isBlank(cfg.value)) out.value = cfg.value;
    if (cfg.locked) out.locked = true;
    return Object.keys(out).length ? out : null;
}

/* ---- Structural edits ---------------------------------------------------- */

/*
  valueNumber is "C3" (a fixed data cell) or "resumo.<n>.<COL>". It goes
  through shiftFormula as an editor reference, so it follows the same rules as
  the formulas; a reference to a removed cell becomes "#REF" and blocks saving.
*/
function valueNumberToRef(settings, valueNumber) {
    if (!valueNumber || valueNumber === "#REF") return null;
    const summary = /^resumo\.(\d+)\.([A-Za-z]+)$/.exec(valueNumber);
    if (summary) return summary[2].toUpperCase() + (editorDataRowCount(settings) + Number(summary[1]));
    if (/^[A-Za-z]+\d+$/.test(valueNumber)) return valueNumber.toUpperCase();
    return null;
}

function refToValueNumber(settings, ref) {
    const match = /^([A-Z]+)(\d+)$/.exec(ref || "");
    if (!match) return "#REF";
    const row = Number(match[2]);
    const dataRows = editorDataRowCount(settings);
    if (row > dataRows) return "resumo." + (row - dataRows) + "." + match[1];
    return match[1] + row;
}

/*
  Runs a structural change: `mutate` changes the arrays, `op` is the matching
  shiftFormula operation (or null). Every formula and valueNumber is rewritten.
*/
function structural(settings, op, mutate) {
    const ref = op ? valueNumberToRef(settings, settings.valueNumber) : null;
    mutate();
    if (!op) return;
    allCellRows(settings).forEach((cells) => {
        cells.forEach((cfg) => {
            if (hasFormula(cfg)) cfg.formula = shiftFormula(cfg.formula, op);
        });
    });
    if (ref) {
        const shifted = shiftFormula(ref, op);
        settings.valueNumber = shifted === "#REF" ? "#REF" : refToValueNumber(settings, shifted);
    }
}

export function insertColumn(settings, at, column) {
    structural(settings, { kind: "insertColumn", at }, () => {
        settings.columns.splice(at, 0, column || newColumn("", "text"));
        allCellRows(settings).forEach((cells) => cells.splice(at, 0, null));
    });
}

export function removeColumn(settings, at) {
    structural(settings, { kind: "removeColumn", at }, () => {
        settings.columns.splice(at, 1);
        allCellRows(settings).forEach((cells) => cells.splice(at, 1));
    });
}

export function moveColumn(settings, from, to) {
    if (from === to || to < 0 || to >= settings.columns.length) return;
    structural(settings, { kind: "moveColumn", from, to }, () => {
        const [col] = settings.columns.splice(from, 1);
        settings.columns.splice(to, 0, col);
        allCellRows(settings).forEach((cells) => {
            const [cell] = cells.splice(from, 1);
            cells.splice(to, 0, cell);
        });
    });
}

export function duplicateColumn(settings, at) {
    const copy = clone(settings.columns[at]);
    if (copy.alias) delete copy.alias;
    const cellsCopy = allCellRows(settings).map((cells) => (cells[at] ? clone(cells[at]) : null));
    structural(settings, { kind: "insertColumn", at: at + 1 }, () => {
        settings.columns.splice(at + 1, 0, copy);
        allCellRows(settings).forEach((cells, index) => cells.splice(at + 1, 0, cellsCopy[index]));
    });
}

function emptyCells(settings) {
    return settings.columns.map(() => null);
}

export function addFixedRow(settings, label = "") {
    const at = editorDataRowCount(settings) + 1;
    structural(settings, { kind: "insertRow", at }, () => {
        settings.rows.items.push({ label, cells: emptyCells(settings) });
    });
}

export function removeFixedRow(settings, index) {
    structural(settings, { kind: "removeRow", at: index + 1 }, () => {
        settings.rows.items.splice(index, 1);
    });
}

export function moveFixedRow(settings, from, to) {
    if (from === to || to < 0 || to >= settings.rows.items.length) return;
    structural(settings, { kind: "moveRow", from: from + 1, to: to + 1 }, () => {
        const [item] = settings.rows.items.splice(from, 1);
        settings.rows.items.splice(to, 0, item);
    });
}

export function addSummaryRow(settings, label = "") {
    settings.rows.summary = settings.rows.summary || [];
    settings.rows.summary.push({ label, cells: emptyCells(settings) });
}

export function removeSummaryRow(settings, index) {
    const at = editorDataRowCount(settings) + index + 1;
    structural(settings, { kind: "removeRow", at }, () => {
        settings.rows.summary.splice(index, 1);
    });
}

/*
  Switching to dynamic keeps the first fixed row as the template and removes
  the others (their references become #REF). Switching to fixed turns the
  template into the first row.
*/
export function setRowsMode(settings, mode) {
    const rows = settings.rows;
    if (rows.mode === mode) return;
    if (mode === "fixed") {
        const template = (rows.template && rows.template.cells) || emptyCells(settings);
        rows.items = [{ label: "", cells: template }];
        delete rows.template;
        delete rows.min;
        delete rows.max;
        delete rows.addLabel;
        rows.mode = "fixed";
        return;
    }
    const items = rows.items || [];
    for (let i = items.length - 1; i >= 1; i--) removeFixedRow(settings, i);
    const first = (rows.items && rows.items[0]) || { cells: emptyCells(settings) };
    // The template has no fixed cell any more: a valueNumber on it is invalid.
    if (settings.valueNumber && /^[A-Z]+\d+$/.test(settings.valueNumber)) settings.valueNumber = "#REF";
    rows.template = { cells: first.cells };
    delete rows.items;
    rows.min = 1;
    rows.max = DEFAULT_DYNAMIC_MAX;
    rows.addLabel = rows.addLabel || "";
    rows.mode = "dynamic";
}

/* ---- Cell input ---------------------------------------------------------- */

/* Accepts "1,5", "1.234,56", "1.5" and "-3". Returns null when not a number. */
export function parseNumberInput(text) {
    if (typeof text === "number") return isFinite(text) ? text : null;
    let s = String(text == null ? "" : text).trim().replace(/\s/g, "");
    if (!s) return null;
    if (s.indexOf(",") !== -1) s = s.replace(/\./g, "").replace(",", ".");
    if (!/^-?\d*\.?\d+$/.test(s) && !/^-?\d+\.$/.test(s)) return null;
    const n = Number(s);
    return isFinite(n) ? n : null;
}

/* Raw text the editor shows for a cell configuration. */
export function cellInputText(cfg, column) {
    if (!cfg) return "";
    if (hasFormula(cfg)) return "=" + cfg.formula;
    if (isBlank(cfg.value)) return "";
    if (column && column.type === "number" && typeof cfg.value === "number") {
        return String(cfg.value).replace(".", ",");
    }
    return String(cfg.value);
}

/*
  Applies what the admin typed in a cell. Returns null on success or an error
  key (i18n suffix) when the text is not acceptable there.
*/
export function setCellInput(settings, target, text) {
    const cells = rowCellsOf(settings, target);
    const column = settings.columns[target.col];
    const previous = cells[target.col] || {};
    const raw = String(text == null ? "" : text);
    const trimmed = raw.trim();
    if (trimmed.startsWith("=")) {
        if (column.type !== "number") return "error_formula_column";
        const formula = trimmed.slice(1).trim();
        cells.splice(target.col, 1, formula ? { formula } : null);
        return null;
    }
    if (target.kind === "summary") {
        if (trimmed) return "error_summary_value";
        cells.splice(target.col, 1, null);
        return null;
    }
    let value = trimmed === "" ? null : raw;
    if (value !== null && column.type === "number") {
        value = parseNumberInput(trimmed);
        if (value === null) return "error_number";
    } else if (value !== null && column.type === "date") {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return "error_date";
        value = trimmed;
    } else if (value !== null && column.type === "select") {
        const option = activeOptions(column).find((o) => o.text === trimmed);
        if (!option) return "error_select";
        value = option.text;
    } else if (value !== null) {
        value = raw.trim();
    }
    cells.splice(target.col, 1, compactCfg({ value, locked: !!previous.locked && value !== null }));
    return null;
}

export function toggleCellLock(settings, target) {
    const cells = rowCellsOf(settings, target);
    const cfg = cells[target.col];
    if (target.kind === "summary" || hasFormula(cfg)) return;
    const next = Object.assign({}, cfg || {}, { locked: !(cfg && cfg.locked) });
    cells.splice(target.col, 1, compactCfg(next));
}

export function activeOptions(column) {
    return ((column && column.options) || []).filter((o) => !o.removed && String(o.text || "").trim());
}

/* ---- Formula helpers for the editor -------------------------------------- */

/* Cells (as editor refs "B2") a formula points at, to highlight them. */
export function referencedRefs(settings, formula) {
    let ast;
    try {
        ast = parseFormula(formula);
    } catch (e) {
        return [];
    }
    const out = {};
    const totalRows = editorDataRowCount(settings) + ((settings.rows.summary || []).length);
    const aliases = {};
    settings.columns.forEach((col, i) => {
        if (col.alias) aliases[String(col.alias).toLowerCase()] = i;
    });
    const addColumn = (c, dataOnly) => {
        const last = dataOnly ? editorDataRowCount(settings) : totalRows;
        for (let r = 1; r <= last; r++) out[columnLetter(c) + r] = true;
    };
    const visit = (node) => {
        if (!node) return;
        if (node.k === "cell") {
            if (node.row === null) addColumn(node.col, true);
            else out[columnLetter(node.col) + node.row] = true;
        } else if (node.k === "range") {
            const cLo = Math.min(node.c1, node.c2);
            const cHi = Math.max(node.c1, node.c2);
            for (let c = cLo; c <= cHi; c++) {
                if (node.r1 === null) addColumn(c, true);
                else {
                    for (let r = Math.min(node.r1, node.r2); r <= Math.max(node.r1, node.r2); r++) {
                        out[columnLetter(c) + r] = true;
                    }
                }
            }
        } else if (node.k === "alias") {
            if (aliases[node.name] !== undefined) addColumn(aliases[node.name], true);
        } else if (node.k === "bin") {
            visit(node.l);
            visit(node.r);
        } else if (node.k === "neg") {
            visit(node.e);
        } else if (node.k === "call") {
            node.args.forEach(visit);
        }
    };
    visit(ast);
    return Object.keys(out);
}

/* Default values of the data rows, used to show live formula results. */
export function sampleInputValues(settings, overrides) {
    const rows = settings.rows;
    const configs = rows.mode === "fixed" ? rows.items.map((i) => i.cells) : [rows.template.cells];
    return configs.map((cells, i) =>
        settings.columns.map((col, j) => {
            const key = i + ":" + j;
            if (overrides && Object.prototype.hasOwnProperty.call(overrides, key)) {
                return overrides[key];
            }
            const cfg = cells[j];
            return cfg && !isBlank(cfg.value) ? cfg.value : null;
        }),
    );
}

export function computeSafe(settings, values) {
    try {
        return computeTable(settings, values);
    } catch (e) {
        return { values: values || [], summary: [], errors: {} };
    }
}

/* ---- Paste from spreadsheets ---------------------------------------------- */

/* Excel / Google Sheets clipboard text (TSV, quoted cells may hold newlines). */
export function parseTsv(text) {
    const src = String(text || "").replace(/\r\n?/g, "\n").replace(/\n+$/, "");
    if (!src) return [];
    const rows = [];
    let row = [];
    let cell = "";
    let i = 0;
    let quoted = false;
    while (i < src.length) {
        const ch = src[i];
        if (quoted) {
            if (ch === '"' && src[i + 1] === '"') {
                cell += '"';
                i += 2;
                continue;
            }
            if (ch === '"') {
                quoted = false;
                i++;
                continue;
            }
            cell += ch;
            i++;
            continue;
        }
        if (ch === '"' && cell === "") {
            quoted = true;
            i++;
            continue;
        }
        if (ch === "\t") {
            row.push(cell);
            cell = "";
        } else if (ch === "\n") {
            row.push(cell);
            rows.push(row);
            row = [];
            cell = "";
        } else {
            cell += ch;
        }
        i++;
    }
    row.push(cell);
    rows.push(row);
    return rows.map((r) => r.map((c) => c.trim()));
}

export function looksLikeTable(text) {
    return /\t/.test(text || "") || /\n./.test(String(text || "").trim());
}

function columnLooksNumeric(matrix, col, fromRow) {
    let seen = 0;
    for (let i = fromRow; i < matrix.length; i++) {
        const v = matrix[i][col];
        if (isBlank(v)) continue;
        if (String(v).startsWith("=")) continue;
        if (parseNumberInput(v) === null) return false;
        seen++;
    }
    return seen > 0;
}

/*
  Builds a new table from pasted cells: the first line becomes the column
  titles. When its first cell is empty the first column holds row titles and
  the table becomes fixed (one row per line); otherwise every other line
  becomes a fixed row with default values. A single header line keeps the
  current row mode.
*/
export function importMatrix(settings, matrix, labels = {}) {
    const lines = (matrix || []).filter((line) => line.some((c) => !isBlank(c)));
    if (!lines.length) return false;
    const header = lines[0];
    const hasRowLabels = lines.length > 1 && isBlank(header[0]);
    const offset = hasRowLabels ? 1 : 0;
    const width = Math.min(
        MAX_COLUMNS,
        Math.max(...lines.map((l) => l.length)) - offset,
    );
    if (width < 1) return false;
    const columns = [];
    for (let j = 0; j < width; j++) {
        const label = header[j + offset] || (labels.column ? labels.column + " " + columnLetter(j) : columnLetter(j));
        const numeric = columnLooksNumeric(lines, j + offset, 1);
        columns.push(newColumn(label, numeric ? "number" : "text"));
    }
    const summary = [];
    settings.columns = columns;
    settings.valueNumber = undefined;
    delete settings.valueNumber;
    const body = lines.slice(1, 1 + MAX_FIXED_ROWS);
    if (!body.length) {
        const rows = settings.rows;
        if (rows.mode === "fixed") rows.items = [{ label: "", cells: emptyCells(settings) }];
        else rows.template = { cells: emptyCells(settings) };
        rows.summary = summary;
        return true;
    }
    settings.rows = { mode: "fixed", items: [], summary };
    body.forEach((line) => {
        const item = { label: hasRowLabels ? line[0] || "" : "", cells: emptyCells(settings) };
        settings.rows.items.push(item);
        const index = settings.rows.items.length - 1;
        for (let j = 0; j < width; j++) {
            const text = line[j + offset];
            if (isBlank(text)) continue;
            setCellInput(settings, { kind: "data", row: index, col: j }, text);
        }
    });
    return true;
}

/*
  Pastes cells starting at a target, adding columns and (fixed) rows when the
  block does not fit. Cells that cannot take the text are skipped; returns the
  number of skipped cells.
*/
export function pasteMatrixAt(settings, anchor, matrix, labels = {}) {
    let skipped = 0;
    (matrix || []).forEach((line, di) => {
        line.forEach((text, dj) => {
            const col = anchor.col + dj;
            if (col >= MAX_COLUMNS) {
                skipped++;
                return;
            }
            while (col >= settings.columns.length) {
                const at = settings.columns.length;
                const numeric = parseNumberInput(text) !== null;
                insertColumn(settings, at, newColumn(
                    labels.column ? labels.column + " " + columnLetter(at) : columnLetter(at),
                    numeric ? "number" : "text",
                ));
            }
            let target;
            if (anchor.kind === "summary") {
                const k = anchor.row + di;
                if (k >= (settings.rows.summary || []).length) {
                    skipped++;
                    return;
                }
                target = { kind: "summary", row: k, col };
            } else if (settings.rows.mode === "fixed") {
                const r = anchor.row + di;
                if (r >= MAX_FIXED_ROWS) {
                    skipped++;
                    return;
                }
                while (r >= settings.rows.items.length) addFixedRow(settings, "");
                target = { kind: "data", row: r, col };
            } else {
                if (di > 0) {
                    skipped++;
                    return;
                }
                target = { kind: "data", row: 0, col };
            }
            if (setCellInput(settings, target, text)) skipped++;
        });
    });
    return skipped;
}

/* ---- Save-time checks ---------------------------------------------------- */

export function valueNumberOptions(settings) {
    const out = [];
    const numberCols = settings.columns
        .map((col, j) => ({ col, j }))
        .filter(({ col }) => col.type === "number");
    (settings.rows.summary || []).forEach((s, k) => {
        numberCols.forEach(({ col, j }) => {
            out.push({
                value: "resumo." + (k + 1) + "." + columnLetter(j),
                ref: columnLetter(j) + (editorDataRowCount(settings) + k + 1),
                row: s.label,
                column: col.label,
                summary: true,
            });
        });
    });
    if (settings.rows.mode === "fixed") {
        settings.rows.items.forEach((item, i) => {
            numberCols.forEach(({ col, j }) => {
                out.push({
                    value: columnLetter(j) + (i + 1),
                    ref: columnLetter(j) + (i + 1),
                    row: item.label,
                    column: col.label,
                    summary: false,
                });
            });
        });
    }
    return out;
}

/*
  Every problem that blocks saving: the engine's validateTableSettings plus
  the contract limits. Returns [{where, message}], `where` is a cell ref, a
  column letter or a section name. `t` translates "forms-table-*" keys.
*/
export function validateTable(settings, t) {
    const tr = (key, args) => (t ? t("forms-table-" + key, args) : key);
    const problems = [];
    const columns = settings.columns || [];
    const rows = settings.rows || {};

    columns.forEach((col, j) => {
        const letter = columnLetter(j);
        if (!COLUMN_TYPES.includes(col.type)) {
            problems.push({ where: letter, message: tr("error_column_type", [letter]) });
        }
        if (col.type === "select" && !activeOptions(col).length) {
            problems.push({ where: letter, message: tr("error_select_options", [letter]) });
        }
        if (col.type === "select") {
            const names = activeOptions(col).map((o) => o.text.trim().toLowerCase());
            if (new Set(names).size !== names.length) {
                problems.push({ where: letter, message: tr("error_select_duplicate", [letter]) });
            }
        }
        if (col.type === "number") {
            const min = col.min;
            const max = col.max;
            if (!isBlank(min) && !isBlank(max) && Number(min) > Number(max)) {
                problems.push({ where: letter, message: tr("error_min_max", [letter]) });
            }
            if (!isBlank(col.decimals) && (Number(col.decimals) < 0 || Number(col.decimals) > 6)) {
                problems.push({ where: letter, message: tr("error_decimals", [letter]) });
            }
        }
    });

    if (rows.mode === "fixed") {
        if (!(rows.items || []).length) problems.push({ where: "rows", message: tr("error_fixed_empty") });
        if ((rows.items || []).length > MAX_FIXED_ROWS) {
            problems.push({ where: "rows", message: tr("error_fixed_max", [MAX_FIXED_ROWS]) });
        }
    } else {
        const min = Number(rows.min);
        const max = Number(rows.max);
        if (!Number.isInteger(min) || min < 0) problems.push({ where: "rows", message: tr("error_rows_min") });
        if (!Number.isInteger(max) || max < 1 || max > MAX_DYNAMIC_ROWS) {
            problems.push({ where: "rows", message: tr("error_rows_max", [MAX_DYNAMIC_ROWS]) });
        } else if (Number.isInteger(min) && min > max) {
            problems.push({ where: "rows", message: tr("error_rows_min_max") });
        }
    }
    if ((rows.summary || []).length > MAX_SUMMARY_ROWS) {
        problems.push({ where: "summary", message: tr("error_summary_max", [MAX_SUMMARY_ROWS]) });
    }

    // Default values must still fit their column after type changes.
    const dataTargets = [];
    const dataRows = editorDataRowCount(settings);
    for (let i = 0; i < dataRows; i++) {
        columns.forEach((col, j) => dataTargets.push({ kind: "data", row: i, col: j }));
    }
    dataTargets.forEach((target) => {
        const cfg = cellCfgOf(settings, target);
        if (!cfg || hasFormula(cfg) || isBlank(cfg.value)) return;
        const col = columns[target.col];
        const ref = cellRefOf(settings, target);
        if (col.type === "number" && parseNumberInput(cfg.value) === null) {
            problems.push({ where: ref, message: tr("error_cell_number", [ref]) });
        } else if (col.type === "select" && !activeOptions(col).some((o) => o.text === cfg.value)) {
            problems.push({ where: ref, message: tr("error_cell_select", [ref]) });
        } else if (col.type === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(String(cfg.value))) {
            problems.push({ where: ref, message: tr("error_cell_date", [ref]) });
        }
    });
    (rows.summary || []).forEach((s, k) => {
        columns.forEach((col, j) => {
            const cfg = s.cells[j];
            if (cfg && !hasFormula(cfg) && (!isBlank(cfg.value) || cfg.locked)) {
                const ref = cellRefOf(settings, { kind: "summary", row: k, col: j });
                problems.push({ where: ref, message: tr("error_summary_value_at", [ref]) });
            }
        });
    });

    // #REF left by removing a referenced column or row.
    const refWheres = {};
    allCellRows(settings).forEach((cells, index) => {
        cells.forEach((cfg, j) => {
            if (hasFormula(cfg) && cfg.formula.indexOf("#REF") !== -1) {
                const target = index < dataRows
                    ? { kind: "data", row: index, col: j }
                    : { kind: "summary", row: index - dataRows, col: j };
                const ref = cellRefOf(settings, target);
                refWheres[ref] = true;
                problems.push({ where: ref, message: tr("error_ref_removed", [ref]) });
            }
        });
    });

    if (settings.valueNumber) {
        const allowed = valueNumberOptions(settings).map((o) => o.value);
        if (!allowed.includes(settings.valueNumber)) {
            problems.push({ where: "valueNumber", message: tr("error_value_number") });
        }
    }

    // The engine messages are already written for people (pt-BR). A #REF
    // formula never parses, so its syntax error would only repeat ours.
    validateTableSettings(normalizeTableSettings(settings)).forEach((p) => {
        if (refWheres[p.where]) return;
        problems.push({ where: p.where, message: p.message, code: p.code });
    });
    return problems;
}

/* ---- Payload ------------------------------------------------------------- */

function numberOrNull(value) {
    if (isBlank(value)) return null;
    const n = Number(String(value).replace(",", "."));
    return isFinite(n) ? n : null;
}

/* The `settings` object sent to the API, exactly in the contract's shape. */
export function normalizeTableSettings(settings) {
    const columns = (settings.columns || []).map((col) => {
        const out = { label: String(col.label || "").trim(), type: col.type || "text" };
        if (col.required) out.required = true;
        if (!isBlank(col.alias)) out.alias = String(col.alias).trim();
        if (!isBlank(col.minWidth)) {
            const w = numberOrNull(col.minWidth);
            if (w !== null) out.minWidth = Math.round(w);
        }
        if (out.type === "number") {
            out.min = numberOrNull(col.min);
            out.max = numberOrNull(col.max);
            const decimals = numberOrNull(col.decimals);
            out.decimals = decimals === null ? null : Math.round(decimals);
            out.prefix = isBlank(col.prefix) ? null : String(col.prefix).trim();
            out.suffix = isBlank(col.suffix) ? null : String(col.suffix).trim();
        }
        if (out.type === "select") {
            out.options = (col.options || [])
                .filter((o) => !(o.new && o.removed))
                .filter((o) => String(o.text || "").trim())
                .map((o, index) => ({ text: String(o.text).trim(), order: index + 1, removed: !!o.removed }));
        }
        return out;
    });
    const n = columns.length;
    const cellsOut = (cells) => {
        const out = [];
        for (let j = 0; j < n; j++) out.push(compactCfg((cells || [])[j]));
        return out;
    };
    const rows = settings.rows || {};
    const rowsOut = { mode: rows.mode === "fixed" ? "fixed" : "dynamic" };
    if (rowsOut.mode === "fixed") {
        rowsOut.items = (rows.items || []).map((item) => ({
            label: String(item.label || "").trim(),
            cells: cellsOut(item.cells),
        }));
    } else {
        rowsOut.min = Number.isFinite(Number(rows.min)) && !isBlank(rows.min) ? Number(rows.min) : 1;
        rowsOut.max = Number.isFinite(Number(rows.max)) && !isBlank(rows.max) ? Number(rows.max) : DEFAULT_DYNAMIC_MAX;
        rowsOut.addLabel = String(rows.addLabel || "").trim();
        rowsOut.template = { cells: cellsOut(rows.template && rows.template.cells) };
    }
    rowsOut.summary = (rows.summary || []).map((s) => ({
        label: String(s.label || "").trim(),
        cells: cellsOut(s.cells),
    }));
    const out = { columns, rows: rowsOut };
    const maxWidth = numberOrNull(settings.maxWidth);
    // Optional, no default: the public form fills its width unless one is set.
    if (maxWidth !== null) out.maxWidth = Math.round(maxWidth);
    if (settings.valueNumber) out.valueNumber = settings.valueNumber;
    return out;
}

/* ---- Stored answers ------------------------------------------------------ */

/*
  The structured value of a table answer: {columns, rows, values, summary}.
  The API exposes it as `table` on each answer row (like `files`); `value`
  as an object or a JSON string is accepted too.
*/
export function tableAnswerData(answer) {
    if (!answer) return null;
    const candidates = [answer.table, answer.tableValue, answer.value];
    for (const candidate of candidates) {
        let data = candidate;
        if (typeof data === "string") {
            try {
                data = JSON.parse(data);
            } catch (e) {
                data = null;
            }
        }
        if (data && Array.isArray(data.columns) && Array.isArray(data.values)) {
            return {
                columns: data.columns,
                rows: data.rows || { mode: "dynamic" },
                values: data.values,
                summary: Array.isArray(data.summary) ? data.summary : [],
            };
        }
    }
    return null;
}

/* pt-BR number text with the column's decimals, prefix and suffix. */
export function formatTableNumber(value, column, withAffixes = true) {
    if (value === null || value === undefined || value === "") return "";
    const n = typeof value === "number" ? value : parseNumberInput(value);
    if (n === null) return String(value);
    const decimals = column && column.decimals !== null && column.decimals !== undefined && column.decimals !== ""
        ? Number(column.decimals)
        : null;
    let text = decimals !== null ? n.toFixed(decimals) : String(parseFloat(n.toPrecision(12)));
    text = text.replace(".", ",");
    if (!withAffixes) return text;
    if (column && column.prefix) text = column.prefix + " " + text;
    if (column && column.suffix) text = text + " " + column.suffix;
    return text;
}

export function formatTableCell(value, column, withAffixes = true) {
    if (value === null || value === undefined || value === "") return "";
    const type = (column && column.type) || "text";
    if (type === "number") return formatTableNumber(value, column, withAffixes);
    if (type === "date") {
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
        return match ? match[3] + "/" + match[2] + "/" + match[1] : String(value);
    }
    return String(value);
}

/* Rows of a stored answer ready to render: [{label, kind, cells:[{text, cfgKind}]}]. */
export function tableDisplayRows(data) {
    const rows = data.rows || {};
    const fixed = rows.mode === "fixed";
    const out = [];
    (data.values || []).forEach((line, i) => {
        const cellsCfg = fixed
            ? (((rows.items || [])[i] || {}).cells || [])
            : ((rows.template || {}).cells || []);
        out.push({
            kind: "data",
            label: fixed ? (((rows.items || [])[i] || {}).label || "") : "",
            cells: data.columns.map((col, j) => ({
                text: formatTableCell(line[j], col),
                raw: line[j],
                formula: hasFormula(cellsCfg[j]),
                locked: !!(cellsCfg[j] && cellsCfg[j].locked),
            })),
        });
    });
    (data.summary || []).forEach((line, k) => {
        const def = (rows.summary || [])[k] || {};
        out.push({
            kind: "summary",
            label: def.label || "",
            cells: data.columns.map((col, j) => ({
                text: formatTableCell(line[j], col),
                raw: line[j],
                formula: hasFormula((def.cells || [])[j]),
                locked: false,
            })),
        });
    });
    return out;
}

export function tableHasRowLabels(data) {
    const rows = (data && data.rows) || {};
    return rows.mode === "fixed" || (data.summary || []).length > 0;
}

function tsvCell(text) {
    const s = String(text == null ? "" : text);
    return /[\t\n"]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

/* Clipboard text that Excel and Google Sheets paste as cells. */
export function tableToTsv(data) {
    const withLabels = tableHasRowLabels(data);
    const lines = [];
    const header = data.columns.map((c) => c.label || "");
    lines.push((withLabels ? [""] : []).concat(header));
    tableDisplayRows(data).forEach((row) => {
        const cells = row.cells.map((cell, j) => {
            const col = data.columns[j];
            return col.type === "number"
                ? formatTableNumber(cell.raw, col, false)
                : formatTableCell(cell.raw, col);
        });
        lines.push((withLabels ? [row.label] : []).concat(cells));
    });
    return lines.map((line) => line.map(tsvCell).join("\t")).join("\n");
}
